import { Link } from 'react-router-dom';
import type { Movie } from '@/mocks/movies';

interface MovieCardProps {
  movie: Movie;
  variant?: 'default' | 'compact' | 'featured';
}

export default function MovieCard({ movie, variant = 'default' }: MovieCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/detail/${movie.slug}`}
        className="group flex gap-3 p-2 rounded-xl hover:bg-background-100 transition-colors"
      >
        <div className="w-20 h-28 flex-shrink-0 rounded-lg overflow-hidden bg-background-200">
          <img
            src={movie.coverImage}
            alt={movie.title}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
        <div className="flex flex-col justify-center min-w-0">
          <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
            {movie.title}
          </h4>
          <div className="flex items-center gap-2 mt-1.5">
            <span className="flex items-center gap-0.5 text-xs text-accent-600">
              <i className="ri-star-fill text-[10px]"></i>
              {movie.rating}
            </span>
            <span className="text-xs text-foreground-500">{movie.year}</span>
          </div>
          <div className="flex flex-wrap gap-1 mt-2">
            {movie.tags.slice(0, 2).map((tag) => (
              <span key={tag} className="px-1.5 py-0.5 text-[10px] rounded-md bg-secondary-100 text-secondary-700 whitespace-nowrap">
                {tag}
              </span>
            ))}
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/detail/${movie.slug}`}
        className="group relative block rounded-xl overflow-hidden bg-background-100"
      >
        <div className="aspect-[16/9] overflow-hidden">
          <img
            src={movie.coverImage}
            alt={movie.title}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
          <div className="flex items-center gap-2 mb-2">
            {movie.isNew && (
              <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-accent-500 text-background-50 whitespace-nowrap">新</span>
            )}
            {movie.isHot && (
              <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-primary-500 text-background-50 whitespace-nowrap">热播</span>
            )}
            <span className="text-xs text-background-200/80">{movie.year}</span>
          </div>
          <h3 className="text-lg md:text-xl font-heading font-bold text-white group-hover:text-accent-300 transition-colors line-clamp-1">
            {movie.title}
          </h3>
          <div className="flex items-center gap-3 mt-2">
            <span className="flex items-center gap-1 text-sm text-accent-400">
              <i className="ri-star-fill text-xs"></i>
              {movie.rating}
            </span>
            <span className="text-xs text-background-300">{movie.duration}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${movie.slug}`}
      className="group block rounded-xl overflow-hidden bg-background-50 border border-background-200/60 hover:border-primary-200 transition-all duration-300"
    >
      <div className="aspect-[2/3] overflow-hidden relative">
        <img
          src={movie.coverImage}
          alt={movie.title}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute top-2 right-2 flex flex-col gap-1">
          {movie.isNew && (
            <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-accent-500 text-background-50 whitespace-nowrap">NEW</span>
          )}
          {movie.isHot && (
            <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-primary-500 text-background-50 whitespace-nowrap">热播</span>
          )}
        </div>
        <div className="absolute top-2 left-2">
          <span className="flex items-center gap-0.5 px-2 py-0.5 text-xs font-semibold rounded-lg bg-black/60 text-accent-400 whitespace-nowrap">
            <i className="ri-star-fill text-[10px]"></i>
            {movie.rating}
          </span>
        </div>
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
          <div className="w-12 h-12 rounded-full bg-primary-500/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all transform scale-75 group-hover:scale-100">
            <i className="ri-play-fill text-background-50 text-xl ml-0.5"></i>
          </div>
        </div>
      </div>
      <div className="p-3 md:p-4">
        <h3 className="text-sm md:text-base font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
          {movie.title}
        </h3>
        <div className="flex items-center gap-2 mt-1.5">
          <span className="text-xs text-foreground-500">{movie.year}</span>
          <span className="text-xs text-foreground-400">·</span>
          <span className="text-xs text-foreground-500">{movie.duration}</span>
        </div>
        <div className="flex flex-wrap gap-1 mt-2">
          {movie.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="px-2 py-0.5 text-[10px] rounded-md bg-secondary-100 text-secondary-700 whitespace-nowrap">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}